<template>
  <div class="p-6 max-w-[1600px] mx-auto space-y-8">
    <!-- Header -->
    <div class="flex flex-col md:flex-row justify-between items-center gap-4">
      <div class="space-y-1">
        <h1 class="text-3xl font-bold tracking-tight text-foreground">{{ displayTitle }}</h1>
        <p class="text-muted-foreground">Suivi et projection de vos actifs financiers.</p>
      </div>
      <div class="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" class="font-bold border-indigo-500/20 text-indigo-600 hover:bg-indigo-500/10" @click="router.push({ name: 'finance_compare' })">
          <GitCompare class="w-4 h-4 mr-2" /> Comparer
        </Button>
        <Button variant="finance" @click="router.push({ name: 'finance_investment_new' })">
          <Plus class="w-4 h-4 mr-2" /> Nouvel Investissement
        </Button>
      </div>
    </div>

    <div v-if="!loading && investments.length === 0" class="flex flex-col items-center justify-center min-h-[400px] bg-muted/50 border-2 border-dashed border-border rounded-2xl p-12 text-center group">
      <div class="w-20 h-20 bg-zinc-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
        <Wallet class="w-10 h-10 text-zinc-400" />
      </div>
      <h3 class="text-xl font-bold text-foreground mb-2">Aucun investissement</h3>
      <p class="text-muted-foreground max-w-sm mb-8 italic">Commencez par ajouter votre premier placement pour visualiser votre patrimoine.</p>
      <Button variant="finance" size="lg" @click="router.push({ name: 'finance_investment_new' })">
        <Plus class="w-5 h-5 mr-2" /> Créer mon premier placement
      </Button>
    </div>
    
    <div v-else class="space-y-8">
      <!-- Stats Bar -->
      <InvestmentStats 
        v-model:selectedCategory="selectedCategory"
        v-model:pastYears="pastYears"
        v-model:futureYears="futureYears"
        :total-valuation="totalValuation"
        :total-plus-value="totalPlusValue"
        :total-invested="totalInvested"
        :filtered-count="filteredInvestments.length"
        :loading="loading"
        :is-amounts-hidden="isAmountsHidden"
        :format-amount="formatAmount"
        :categories-tree="categoriesTree"
      />

      <!-- Evolution Chart -->
      <EvolutionChartCard 
        :evolution-data="evolutionData"
        :loading-evolution="loadingEvolution"
        :is-refreshing-chart="isRefreshingChart"
        :evolution-error="evolutionError"
        :selected-category="selectedCategory"
        :hidden-extra-categories="hiddenExtraCategories"
        @toggle-category-visibility="toggleCategoryVisibility"
      />

      <!-- Investment Grid -->
      <div class="space-y-4">
        <h2 class="text-xl font-bold tracking-tight text-foreground border-l-4 border-primary pl-4">Liste des Placements ({{ loading ? '...' : filteredInvestments.length }})</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" role="list">
          <template v-if="!loading">
            <InvestmentCard 
              v-for="inv in filteredInvestments" 
              :key="inv.id" 
              :inv="inv"
              :is-amounts-hidden="isAmountsHidden"
              :format-amount="formatAmount"
            />
          </template>

          <template v-else>
            <Card v-for="i in 3" :key="i" class="flex flex-col h-[280px] bg-zinc-50/50 border-zinc-100 animate-pulse" aria-busy="true">
              <CardHeader class="pb-2">
                <div class="flex justify-between items-start">
                  <div class="space-y-2">
                    <Skeleton class="h-5 w-40" aria-hidden="true" />
                    <Skeleton class="h-3 w-16" aria-hidden="true" />
                  </div>
                  <Skeleton class="w-16 h-4 rounded-full" aria-hidden="true" />
                </div>
              </CardHeader>
              <CardContent class="mt-4 space-y-6">
                 <Skeleton class="h-16 w-full rounded-lg" aria-hidden="true" />
                 <Skeleton class="h-10 w-full rounded-md" aria-hidden="true" />
              </CardContent>
            </Card>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Plus, Wallet, GitCompare } from 'lucide-vue-next'

// UI Components
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useGlobalState } from '../../composables/useGlobalState.js'
import { slugify } from '@/lib/utils'

// Sub-components
import InvestmentStats from './InvestmentList/InvestmentStats.vue'
import EvolutionChartCard from './InvestmentList/EvolutionChartCard.vue'
import InvestmentCard from './InvestmentList/InvestmentCard.vue'

const router = useRouter()
const route = useRoute()
const { isAmountsHidden, categories: rawCategories, fetchCategories, getAuthHeader } = useGlobalState()
const investments = ref([])
const loading = ref(true)
const error = ref('')

const selectedCategory = ref(null)

const categoriesTree = computed(() => {
    const list = rawCategories.value
    let rootId = null
    
    const catId = parseInt(route.params.id)
    if (catId) {
        let current = list.find(c => c.id === catId)
        while (current && current.parentId !== null) {
            current = list.find(c => c.id === current.parentId)
        }
        if (current) rootId = current.id
    }

    const buildTree = (list, parentId = null, depth = 0) => {
        let tree = []
        list.filter(c => c.parentId === parentId).forEach(c => {
            tree.push({ ...c, displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (depth > 0 ? '└ ' : '') + c.name })
            tree = tree.concat(buildTree(list, c.id, depth + 1))
        })
        return tree
    }

    if (rootId) {
        const root = list.find(c => c.id === rootId)
        if (!root) return []
        return [
            { ...root, displayName: root.name },
            ...buildTree(list, rootId, 1)
        ]
    }
    return buildTree(list, null)
})

const displayTitle = computed(() => {
    const list = rawCategories.value
    if (!list.length) return 'Vos Investissements'
    const catId = parseInt(route.params.id)
    if (!catId) return 'Vos Investissements'
    let current = list.find(c => c.id === catId)
    while (current && current.parentId !== null) {
        current = list.find(c => c.id === current.parentId)
    }
    return current ? `Vos Investissements : ${current.name}` : 'Vos Investissements'
})

const getDescendantCategoryIds = (catId) => {
    let ids = [catId]
    const children = rawCategories.value.filter(c => c.parentId === catId)
    children.forEach(c => {
        ids = ids.concat(getDescendantCategoryIds(c.id))
    })
    return ids
}

const filteredInvestments = computed(() => {
    if (!selectedCategory.value) return investments.value
    const validIds = getDescendantCategoryIds(selectedCategory.value)
    return investments.value.filter(inv => inv.category && validIds.includes(inv.category.id))
})

const totalValuation = computed(() => filteredInvestments.value.filter(i => !i.ignoreInGlobalSums && !i.isSimulation).reduce((sum, inv) => sum + parseFloat(inv.currentValue || 0), 0))
const totalInvested = computed(() => filteredInvestments.value.filter(i => !i.ignoreInGlobalSums && !i.isSimulation).reduce((sum, inv) => sum + parseFloat(inv.investedAmount || 0), 0))

const totalPlusValue = computed(() => {
    return filteredInvestments.value
        .filter(i => !i.ignoreInGlobalSums && !i.ignorePnL && !i.isSimulation)
        .reduce((sum, inv) => sum + (parseFloat(inv.currentValue || 0) - parseFloat(inv.investedAmount || 0)), 0)
})

watch([() => route.params.id, () => rawCategories.value], ([newVal, cats]) => {
    if (newVal) {
        const id = parseInt(newVal)
        selectedCategory.value = id
        if (cats && cats.length > 0) {
            const category = cats.find(c => c.id === id)
            if (category) {
                const expectedSlug = slugify(category.name)
                const currentSlug = route.params.name || ''
                if (currentSlug !== expectedSlug) {
                    router.replace({ name: 'finance_category', params: { id, name: expectedSlug } })
                }
            }
        }
    } else {
        selectedCategory.value = null
    }
}, { immediate: true })

const formatAmount = (val) => {
    const num = parseFloat(val)
    if (isNaN(num)) return '0,00'
    return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)
}

const fetchInvestments = async () => {
    const res = await fetch('/api/investments', { headers: getAuthHeader() })
    if (res.ok) {
        investments.value = await res.json()
    } else {
        error.value = 'Erreur lors de la récupération.'
    }
    loading.value = false
}

const pastYears = ref(5)
const futureYears = ref(10)
const evolutionData = ref([])
const loadingEvolution = ref(false)
const isRefreshingChart = ref(false)
const evolutionError = ref('')
const hiddenExtraCategories = ref(new Set())

const toggleCategoryVisibility = (catName) => {
    const next = new Set(hiddenExtraCategories.value)
    if (next.has(catName)) next.delete(catName)
    else next.add(catName)
    hiddenExtraCategories.value = next
}

const fetchEvolution = async () => {
    isRefreshingChart.value = evolutionData.value.length > 0
    loadingEvolution.value = evolutionData.value.length === 0
    evolutionError.value = ''
    try {
        const catParam = selectedCategory.value ? `&categoryIds=${selectedCategory.value}` : ''
        const url = `/api/investments/evolution?pastYears=${pastYears.value}&futureYears=${futureYears.value}${catParam}`
        const res = await fetch(url, { headers: getAuthHeader() })
        if (res.ok) {
            const data = await res.json()
            evolutionData.value = data
            const cacheKey = `fin_evolution_cache_${pastYears.value}_${futureYears.value}_${selectedCategory.value ?? 'all'}`
            localStorage.setItem(cacheKey, JSON.stringify(data))
        }
    } catch(e) {
        console.error('Evolution fetch failed', e)
    }
    loadingEvolution.value = false
    isRefreshingChart.value = false
}

const loadEvolutionFromCache = () => {
    const cacheKey = `fin_evolution_cache_${pastYears.value}_${futureYears.value}_${selectedCategory.value ?? 'all'}`
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
        evolutionData.value = JSON.parse(cached)
    }
}

watch([pastYears, futureYears, selectedCategory], () => {
    loadEvolutionFromCache()
    fetchEvolution()
}, { immediate: true })

onMounted(() => {
    fetchInvestments()
    fetchCategories()
    fetchEvolution()
})
</script>

<style scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
